'use client';

import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '@glomopay/utils';

/**
 * Canonical Drawer (KAN-8027). vaul won over the radix `sheet.tsx` it replaced:
 * every call site in this repo is a bottom sheet, and vaul is the only one of the
 * two that implements the drag-to-dismiss gesture that bottom sheets need.
 * Pulse frame 1506:2 additionally specs a right-edge panel (sm/md/lg widths) —
 * nothing renders that yet, so the `side`/width axes are deliberately absent.
 * Add them when a call site needs one, not before.
 */
const Drawer = ({ shouldScaleBackground = true, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & { isConstrained?: boolean }
>(({ className, isConstrained = false, ...props }, ref) => (
  <DrawerPrimitive.Overlay ref={ref} className={cn('bg-bg-overlay inset-0 z-50', isConstrained ? 'absolute' : 'fixed', className)} {...props} />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & { dismissible?: boolean; showOverlay?: boolean }
>(({ className, dismissible, showOverlay = true, children, ...props }, ref) => (
  <DrawerPortal>
    {showOverlay && <DrawerOverlay />}
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        'bg-bg-default border-border-default pb-safe-bottom fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-lg border',
        className,
      )}
      {...props}>
      {dismissible && <div className='bg-bg-muted mx-auto mt-4 h-2 w-[100px] rounded-full' />}
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

const DrawerConstrainedContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & { dismissible?: boolean; showOverlay?: boolean }
>(({ className, dismissible, showOverlay = true, children, ...props }, ref) => (
  <>
    {showOverlay && <DrawerOverlay isConstrained={true} className='rounded-xl' />}
    <DrawerPrimitive.Content
      ref={ref}
      className={cn('bg-bg-default absolute inset-x-0 bottom-0 z-50 flex h-auto flex-col rounded-t-lg', className)}
      {...props}>
      {dismissible && <div className='bg-bg-muted mx-auto mt-4 h-2 w-[100px] rounded-full' />}
      {children}
    </DrawerPrimitive.Content>
  </>
));
DrawerConstrainedContent.displayName = 'DrawerConstrainedContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<React.ElementRef<typeof DrawerPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>>(
  ({ className, ...props }, ref) => <DrawerPrimitive.Title ref={ref} className={cn('text-heading-md text-text-primary', className)} {...props} />,
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={cn('text-body-md text-text-secondary', className)} {...props} />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerConstrainedContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
